import React, {
  useEffect,
  useState,
  useMemo,
  Suspense,
  lazy,
  useRef
} from "react";
import { BiBriefcaseAlt } from "react-icons/bi";
import {
  LuUsers,
  LuBuilding2,
  LuCreditCard,
  LuChevronDown,
  LuDollarSign,
  LuFileText,
  LuUserCheck,
  LuShieldCheck,
  LuCheck
} from "react-icons/lu";
import {
  APPLY_ID_STATUS_POLL_INTERVAL,
  COMPLETED,
  INPROGRESS,
  PROPERTY_OWNED,
  START,
  USERS_ROLE,
  WAITING_START
} from "../../../utils/constants/appConstants";
import "./steps-style.scss";
import {
  buttonName,
  newOnboardingSteps,
  responseStatus
} from "../../../locales/en";
import privateAxios from "../../../utils/axiosPrivate";
import { getCreateOnboardingStepURL } from "../../../utils/constants/apiEndPoints";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchApplyIdStatus,
  getAboutYourBusiness,
  getAnnatureCheckStatus,
  getBusinessDetailsData,
  getDirectorRosterData,
  getOnboardingOwnerData,
  getOnboardingRoleData,
  getOnboardingStepsByUser
} from "../../../redux/onboardingSlice";
import { showToast } from "../../../utils/utils";
import { mapBusinessTypeFromPayload } from "../../../utils/constants/businessTypeConfig";
import { getConnectedSoftware } from "../../../redux/loginSlice";
import { scrollStepIntoView } from "../../../utils/scrollStepIntoView";
import { directorNames, selectWaitingDirectors } from "./waitingDirectors";

// Lazy load components for code splitting
const Business = lazy(() => import("./business"));
const Role = lazy(() => import("./role"));
const Identity = lazy(() => import("./identity"));
const PeopleBusiness = lazy(() => import("./people-business"));
const PropertyDetails = lazy(() => import("./property-details"));
const SupportingCompanyDetails = lazy(
  () => import("./supporting-company-details")
);
const TrustDetails = lazy(() => import("./trust-details"));
const SupportingTrustDetails = lazy(() => import("./supporting-trust-details"));
const ShareholderTrustDetails = lazy(
  () => import("./shareholder-trust-details")
);
const Repayment = lazy(() => import("./repayment"));
const Agree = lazy(() => import("./agree"));
const VerifyId = lazy(() => import("./verify-id"));
const WhatYouAgree = lazy(() => import("./what-you-agree"));

// Loading Fallback Component
const StepLoadingFallback = () => (
  <div className="steps-loading-fallback">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">{buttonName?.loading}</span>
    </div>
  </div>
);

// Collects the distinct entity names of the >=25% shareholders of one holding
// type. A shareholder edited through the per-person update endpoint can live
// only in shareholderAndDirector.shareholders (e.g. an NZBN-lookup-seeded
// roster, where the top-level list is empty and the edit cannot be mirrored
// into it), so both lists are searched; identical names are deduped since two
// holdings through the same entity share one record and one card.
const collectShareholderEntityNames = (
  businessDetails,
  typeMatches,
  nameKey
) => {
  const qualifies = (shareholder) => {
    const type = String(shareholder?.shareholdingType || "")
      .replace(/\s/g, "")
      .toLowerCase();
    return (
      typeMatches(type) &&
      parseFloat(shareholder?.share_percentage) >= 25 &&
      shareholder?.[nameKey]?.trim()
    );
  };

  const names = [];
  const lists = [
    businessDetails?.shareholders,
    businessDetails?.shareholderAndDirector?.shareholders
  ];
  for (const list of lists) {
    for (const shareholder of Array.isArray(list) ? list : []) {
      if (!qualifies(shareholder)) continue;
      const name = shareholder[nameKey].trim();
      if (
        !names.some((existing) => existing.toLowerCase() === name.toLowerCase())
      ) {
        names.push(name);
      }
    }
  }
  return names;
};

const Steps = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.userManagement?.profile?.id);
  const {
    onBoardingStepsData,
    onboardingRoleData,
    applyIdStatusData,
    aboutYourBusinessData,
    businessDetailsData,
    directorRosterData,
    ownersData
  } = useSelector((state) => state.onboarding);
  const intervalRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [tabFacilitySize, setTabFacilitySize] = useState(null);
  const [tabUserRole, setTabUserRole] = useState("");
  const [tabBusinessType, setTabBusinessType] = useState("Limited Company");
  const [tabTrustName, setTabTrustName] = useState("");
  const [tabPropertyOwned, setTabPropertyOwned] = useState(
    PROPERTY_OWNED?.PERSONALLY_OWNED
  );
  const [tabPropertyTrustName, setTabPropertyTrustName] = useState("");
  const [tabPropertyCompanyName, setTabPropertyCompanyName] = useState("");
  const [loadedTabs, setLoadedTabs] = useState(new Set([0]));
  const autoAdvancedStepsRef = React.useRef(new Set());
  const isManualStepClickRef = useRef(false);
  const shouldScrollToActiveStepRef = useRef(false);
  const stepRefs = useRef({});

  useEffect(() => {
    if (onboardingRoleData) {
      setTabUserRole(onboardingRoleData.yourRole || "");
    }
  }, [onboardingRoleData]);

  useEffect(() => {
    if (aboutYourBusinessData) {
      setTabFacilitySize(aboutYourBusinessData.facilitySize || null);
    }
  }, [aboutYourBusinessData]);

  useEffect(() => {
    if (!businessDetailsData) return;

    setTabBusinessType(
      mapBusinessTypeFromPayload(businessDetailsData.businessType)
    );
    setTabTrustName(businessDetailsData.trustName || "");
  }, [businessDetailsData]);

  useEffect(() => {
    if (!ownersData) return;

    setTabPropertyOwned(
      ownersData.ownershipType || PROPERTY_OWNED?.PERSONALLY_OWNED
    );
    setTabPropertyTrustName(ownersData.trustEntityName || "");
    setTabPropertyCompanyName(ownersData.companyName || "");
  }, [ownersData]);

  // Memoize conditional values to prevent unnecessary recalculations
  const isLargeFacility = useMemo(
    () => ["30-100", "100-250", "above-250"].includes(tabFacilitySize),
    [tabFacilitySize]
  );

  const isOwner = useMemo(
    () => tabUserRole === USERS_ROLE.OWNER_OR_DIRECTOR,
    [tabUserRole]
  );

  const isOwnerConsentAccepted = useMemo(
    () => isOwner && !!onboardingRoleData?.isCreditConsentAccepted,
    [isOwner, onboardingRoleData]
  );

  const isEmployeeOrAdvisor = useMemo(
    () =>
      tabUserRole === USERS_ROLE.EMPLOYEE ||
      tabUserRole === USERS_ROLE.EXTERNAL_ADVISOR,
    [tabUserRole]
  );

  const isBusinessTrustOwned = useMemo(
    () => tabBusinessType === "Trading Trust" && !!tabTrustName?.trim(),
    [tabBusinessType, tabTrustName]
  );

  const isPropertyTrustOwned = useMemo(
    () =>
      tabPropertyOwned === PROPERTY_OWNED?.OWNED_BY_TRUST &&
      !!tabPropertyTrustName?.trim(),
    [tabPropertyOwned, tabPropertyTrustName]
  );

  const isCompanyOwned = useMemo(
    () =>
      tabPropertyOwned === PROPERTY_OWNED?.OWNED_BY_COMPANY &&
      !!tabPropertyCompanyName?.trim(),
    [tabPropertyOwned, tabPropertyCompanyName]
  );

  const isShareholderCompanyRequired = useMemo(
    () => Boolean(businessDetailsData?.supportingCompanyRequired),
    [businessDetailsData]
  );

  // Every >=25% company-held shareholder needs its company's own Supporting
  // Company Details card, and every >=25% trust-held one its trust's own
  // Trust Details card - one card per distinct entity name.
  const shareholderCompanyNames = useMemo(
    () =>
      collectShareholderEntityNames(
        businessDetailsData,
        (type) => type === "heldbyacompany" || type === "companyheld",
        "companyName"
      ),
    [businessDetailsData]
  );

  const shareholderTrustNames = useMemo(
    () =>
      collectShareholderEntityNames(
        businessDetailsData,
        (type) => type === "heldbyatrust" || type === "trustheld",
        "trustName"
      ),
    [businessDetailsData]
  );

  // The supporting-company cards: the property-owning company (when the
  // property step named one) plus one per >=25% company-held shareholder. The
  // bare supportingCompanyRequired flag keeps a single unnamed card for legacy
  // data where the flag was set without a resolvable company name.
  const supportingCompanyNames = useMemo(() => {
    const names = [];
    if (isCompanyOwned) {
      names.push(tabPropertyCompanyName.trim());
    }
    for (const name of shareholderCompanyNames) {
      if (
        !names.some((existing) => existing.toLowerCase() === name.toLowerCase())
      ) {
        names.push(name);
      }
    }
    if (!names.length && isShareholderCompanyRequired) {
      names.push("");
    }
    return names;
  }, [
    isCompanyOwned,
    tabPropertyCompanyName,
    shareholderCompanyNames,
    isShareholderCompanyRequired
  ]);

  const steps = Array.isArray(onBoardingStepsData) ? onBoardingStepsData : [];

  const stepStatusMap = useMemo(() => {
    return steps.reduce((acc, item) => {
      if (item?.step) {
        acc[item.step] = item.status;
      }
      return acc;
    }, {});
  }, [steps]);

  const isStepCompleted = (title) =>
    stepStatusMap[title]?.toLowerCase() === COMPLETED.toLowerCase();

  const isStepInProgress = (title) =>
    stepStatusMap[title]?.toLowerCase() === INPROGRESS.toLowerCase();

  // Agreements are filled with the legal name the ID check captures, so the
  // signable tile stays locked until the verification has actually completed -
  // the live ApplyID status (shared by whoever verifies on this account, so it
  // also covers the director an Employee/Advisor is waiting on), or the Verify
  // Identity step already recorded as done.
  const isIdentityVerified = useMemo(
    () =>
      Boolean(applyIdStatusData?.status) ||
      stepStatusMap["Verify Identity"]?.toLowerCase() ===
        COMPLETED.toLowerCase(),
    [applyIdStatusData, stepStatusMap]
  );

  // The identity check and the agreement are one pair for an owner: the agreement is filled
  // with the name the check captures, so an agreement tile shown without an identity tile is
  // a greyed step the applicant has no way to unlock. Both are driven by one predicate, so
  // they appear together once consent is stored - or once the check is already done, which
  // keeps the agreement in place for an account that verified without the flag on record.
  const isOwnerIdentityFlowVisible = useMemo(
    () => isOwner && (isOwnerConsentAccepted || isIdentityVerified),
    [isOwner, isOwnerConsentAccepted, isIdentityVerified]
  );

  const resolveStepName = (step) => {
    if (step?.component === SupportingTrustDetails) {
      return "Property Trust Details";
    }
    if (step?.component === TrustDetails) {
      return "Trading Trust Details";
    }
    if (step?.component === ShareholderTrustDetails) {
      return "Shareholder Trust Details";
    }
    return step?.title;
  };

  // Memoize requiredSteps to prevent unnecessary re-renders
  const requiredSteps = useMemo(
    () => [
      {
        icon: <BiBriefcaseAlt />,
        title: "About Your Business",
        status: stepStatusMap["About Your Business"],
        component: Business,
        props: {
          setTabFacilitySize,
          onContinue: () => handleStepComplete("About Your Business")
        }
      },
      {
        icon: <LuUsers />,
        title: "Your Role",
        component: Role,
        status: stepStatusMap["Your Role"],
        props: {
          onContinue: () => handleStepComplete("Your Role")
        }
      },
      ...(isOwnerIdentityFlowVisible
        ? [
            {
              icon: <LuUserCheck />,
              title: "Verify Identity",
              status: stepStatusMap["Verify Identity"],
              component: Identity,
              props: {
                onContinue: () => handleStepComplete("Verify Identity")
              }
            }
          ]
        : []),
      {
        icon: <LuBuilding2 />,
        title: "People In The Business",
        status: stepStatusMap["People In The Business"],
        component: PeopleBusiness,
        props: {
          setTabBusinessType,
          setTabTrustName,
          onContinue: () => handleStepComplete("People In The Business")
        }
      },
      ...(isLargeFacility
        ? [
            {
              icon: <LuDollarSign />,
              title: "Property Details",
              component: PropertyDetails,
              props: {
                setTabPropertyOwned,
                setTabPropertyTrustName,
                setTabPropertyCompanyName,
                onContinue: () => handleStepComplete("Property Details")
              }
            }
          ]
        : []),
      ...supportingCompanyNames.map((name) => ({
        icon: <LuFileText />,
        title: "Supporting Company Details",
        subtitle: name,
        status: stepStatusMap["Supporting Company Details"],
        component: SupportingCompanyDetails,
        props: {
          companyName: name,
          onContinue: () => handleStepComplete("Supporting Company Details")
        }
      })),
      ...(isPropertyTrustOwned
        ? [
            {
              icon: <LuFileText />,
              title: "Trust Details",
              subtitle: tabPropertyTrustName,
              status: stepStatusMap["Trust Details"],
              component: SupportingTrustDetails,
              props: {
                trustName: tabPropertyTrustName,
                onContinue: () =>
                  handleStepComplete("Trust Details", SupportingTrustDetails)
              }
            }
          ]
        : []),
      ...(isBusinessTrustOwned
        ? [
            {
              icon: <LuFileText />,
              title: "Trust Details",
              subtitle: tabTrustName,
              status: stepStatusMap["Trust Details"],
              component: TrustDetails,
              props: {
                trustName: tabTrustName,
                onContinue: () =>
                  handleStepComplete("Trust Details", TrustDetails)
              }
            }
          ]
        : []),
      ...shareholderTrustNames.map((name) => ({
        icon: <LuFileText />,
        title: "Trust Details",
        subtitle: name,
        status: stepStatusMap["Shareholder Trust Details"],
        component: ShareholderTrustDetails,
        props: {
          trustName: name,
          onContinue: () =>
            handleStepComplete("Trust Details", ShareholderTrustDetails)
        }
      })),
      {
        icon: <LuCreditCard />,
        title: "Funding and Repayments",
        component: Repayment,
        props: {
          onContinue: () => handleStepComplete("Funding and Repayments")
        }
      },
      ...(isOwnerIdentityFlowVisible
        ? [
            {
              icon: <LuFileText />,
              title: "What You Agree To",
              subtitle: isIdentityVerified
                ? undefined
                : newOnboardingSteps?.agreeLockedSubtitle,
              status: stepStatusMap["What You Agree To"],
              component: Agree,
              // Visible alongside the Verify Identity tile, but greyed out and
              // unclickable until the ID check completes (FV2-725).
              disabled: !isIdentityVerified,
              props: {
                onContinue: () => handleStepComplete("What You Agree To")
              }
            }
          ]
        : [])
    ],
    [
      isLargeFacility,
      isOwnerIdentityFlowVisible,
      isIdentityVerified,
      isBusinessTrustOwned,
      isPropertyTrustOwned,
      supportingCompanyNames,
      shareholderTrustNames,
      tabTrustName,
      tabPropertyTrustName,
      stepStatusMap
    ]
  );

  // The people an Employee or External Advisor is waiting on. Both waiting
  // cards name them, so the list is derived once here and handed to each card.
  const waitingDirectors = useMemo(
    () => selectWaitingDirectors(directorRosterData),
    [directorRosterData]
  );

  // One pair of cards however many directors there are: the two cards are the
  // two outstanding steps, not one person each, and the ApplyID status the
  // Verify Identity card reads is account-wide, so per-director cards would
  // claim progress the API cannot report. Every director is named on the card
  // instead. With no name on record the subtitle falls back to the bare role.
  const waitingSubtitle =
    directorNames(waitingDirectors) ||
    newOnboardingSteps?.waitingDirectorSubtitle;

  // Memoize waitingSteps to prevent unnecessary re-renders
  const waitingSteps = useMemo(
    () =>
      isEmployeeOrAdvisor
        ? [
            {
              icon: <LuShieldCheck />,
              title: "Verify Identity",
              subtitle: waitingSubtitle,
              component: VerifyId,
              completed: isIdentityVerified,
              props: { directors: waitingDirectors }
            },
            // Open from the start, unlike the Owner's signable step: this card
            // sends a reminder rather than a document, and an Employee or
            // External Advisor has to be able to chase the director before the
            // ID check is done as well as after it.
            {
              icon: <LuFileText />,
              title: "What You Agree To",
              subtitle: waitingSubtitle,
              component: WhatYouAgree,
              props: { directors: waitingDirectors }
            }
          ]
        : [],
    [isEmployeeOrAdvisor, isIdentityVerified, waitingDirectors, waitingSubtitle]
  );

  const toggleAccordion = (index, label, component) => {
    const isOpening = activeIndex !== index;
    isManualStepClickRef.current = true;
    shouldScrollToActiveStepRef.current = true;
    setActiveIndex(isOpening ? index : null);
    if (isOpening) {
      setLoadedTabs((prev) => new Set(prev).add(index));
      if (label) {
        handleStepClick(label, component);
      }
    }
  };

  // The write in flight, or queued, per step. A step is created by reading the
  // list and writing when the read finds nothing, and that list is refreshed a
  // request later - so two writes raised in the same tick both read a list
  // without the step and both create it. The second row then broke every later
  // save for that step, until the lookup stopped demanding a single row.
  //
  // Rather than drop the second write - which loses a COMPLETED save raised while
  // the open write is still pending, leaving the step IN_PROGRESS on the server -
  // each write for a step runs after any earlier one for that same step. The open
  // write lands first, so the completion write updates that row instead of
  // inserting a duplicate. Different steps keep independent chains.
  const stepWriteChainsRef = useRef(new Map());

  const writeStep = (payload) => {
    const runWrite = async () => {
      try {
        await privateAxios.put(getCreateOnboardingStepURL, payload);
        dispatch(getOnboardingStepsByUser(Number(userId)));
      } catch (error) {
        showToast(
          error?.response?.data?.message ?? responseStatus?.errorText,
          responseStatus?.error
        );
      }
    };

    const previous = stepWriteChainsRef.current.get(payload.step);
    // No earlier write for this step: run now, so the request fires this tick.
    // Otherwise queue behind it, isolating a prior failure so this write still runs.
    const chained = previous
      ? previous.catch(() => {}).then(runWrite)
      : runWrite();

    stepWriteChainsRef.current.set(payload.step, chained);
    chained.finally(() => {
      if (stepWriteChainsRef.current.get(payload.step) === chained) {
        stepWriteChainsRef.current.delete(payload.step);
      }
    });

    return chained;
  };

  const handleStepClick = async (step, component) => {
    let stepName = step;
    if (component === SupportingTrustDetails) {
      stepName = "Property Trust Details";
    } else if (component === TrustDetails) {
      stepName = "Trading Trust Details";
    } else if (component === ShareholderTrustDetails) {
      stepName = "Shareholder Trust Details";
    }

    const existingStep = steps
      ? steps.find(
          (item) =>
            item?.step === stepName &&
            (item?.status?.toLowerCase() === INPROGRESS.toLowerCase() ||
              item?.status?.toLowerCase() === COMPLETED.toLowerCase())
        )
      : undefined;

    if (existingStep) {
      return;
    }

    await writeStep({ userAccountId: userId, step: stepName });
  };

  const handleStepComplete = async (step, component) => {
    // Completing a step auto-advances to the next one; that move is the
    // user's doing, so it should bring the new step into view.
    shouldScrollToActiveStepRef.current = true;
    let stepName = step;
    if (component === SupportingTrustDetails) {
      stepName = "Property Trust Details";
    } else if (component === TrustDetails) {
      stepName = "Trading Trust Details";
    } else if (component === ShareholderTrustDetails) {
      stepName = "Shareholder Trust Details";
    }

    await writeStep({
      userAccountId: userId,
      step: stepName,
      status: COMPLETED
    });
  };

  useEffect(() => {
    if (activeIndex === null || activeIndex === undefined) return;

    if (isManualStepClickRef.current) {
      isManualStepClickRef.current = false;
      return;
    }

    const currentStep = requiredSteps[activeIndex];
    if (!currentStep) return;

    if (
      isStepCompleted(resolveStepName(currentStep)) &&
      !autoAdvancedStepsRef.current.has(activeIndex)
    ) {
      autoAdvancedStepsRef.current.add(activeIndex);

      let nextIndex = activeIndex + 1;
      let nextStep = requiredSteps[nextIndex];
      while (
        nextStep &&
        (isStepCompleted(resolveStepName(nextStep)) || nextStep.disabled)
      ) {
        nextIndex += 1;
        nextStep = requiredSteps[nextIndex];
      }

      if (!nextStep) {
        setActiveIndex(null);
        return;
      }

      setActiveIndex(nextIndex);
      setLoadedTabs((prev) => new Set(prev).add(nextIndex));
      handleStepClick(nextStep.title, nextStep.component);
    }
  }, [activeIndex, requiredSteps, stepStatusMap]);

  const hasRedirectedToDashboardRef = useRef(false);

  useEffect(() => {
    if (hasRedirectedToDashboardRef.current) return;

    // The step list is built from role/facility data that loads asynchronously;
    // until both have arrived it can be missing conditional steps, and a
    // half-built list must never read as "everything done".
    if (!onboardingRoleData || !aboutYourBusinessData) return;
    if (!steps.length) return;

    // The list reads that data through tabUserRole/tabFacilitySize, which
    // effects copy out one commit after it lands. In that commit the list is
    // still the role-less one - no Verify Identity, no What You Agree To, no
    // Property Details - so redirecting on it would take an Owner past the
    // unsigned agreement (FV2-843).
    if (tabUserRole !== (onboardingRoleData.yourRole || "")) return;
    if (tabFacilitySize !== (aboutYourBusinessData.facilitySize || null))
      return;

    const allTasksCompleted = requiredSteps.every((step) =>
      isStepCompleted(resolveStepName(step))
    );
    if (!allTasksCompleted) return;

    hasRedirectedToDashboardRef.current = true;
    showToast(newOnboardingSteps?.allTasksCompleted, responseStatus?.success);
    navigate("/invoice");
  }, [
    requiredSteps,
    stepStatusMap,
    onboardingRoleData,
    aboutYourBusinessData,
    tabUserRole,
    tabFacilitySize,
    steps.length,
    navigate
  ]);

  useEffect(() => {
    if (!shouldScrollToActiveStepRef.current) return;
    // Consume the request even when there is nothing to scroll to, so it
    // cannot leak into a later programmatic step change.
    shouldScrollToActiveStepRef.current = false;

    if (activeIndex === null || activeIndex === undefined) return;

    const node = stepRefs.current[activeIndex];
    if (!node) return;

    // The card is still growing when this runs - the step being left collapses
    // over 0.35s and this step's lazy chunk has yet to replace its spinner - so
    // the scroll has to keep re-measuring rather than aim once (FV2-719).
    return scrollStepIntoView(node);
  }, [activeIndex]);

  useEffect(() => {
    if (userId) {
      dispatch(getAboutYourBusiness(Number(userId)));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (userId) {
      dispatch(getBusinessDetailsData(userId));
    }
  }, [dispatch, userId]);

  // The waiting cards name the directors, and People In The Business can add,
  // edit or remove one while this page is open. Every write there re-reads
  // businessDetailsData, and the roster is derived from that same record, so
  // following that value names the current directors without a page reload.
  // It costs one extra read on first load, when the record arrives after mount.
  useEffect(() => {
    if (userId) {
      dispatch(getDirectorRosterData(userId));
    }
  }, [dispatch, userId, businessDetailsData]);

  useEffect(() => {
    if (userId) {
      dispatch(getOnboardingRoleData(Number(userId)));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (userId) {
      dispatch(getOnboardingStepsByUser(Number(userId)));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (userId && requiredSteps.length) {
      handleStepClick(requiredSteps[0].title, requiredSteps[0].component);
    }
  }, [userId]);

  useEffect(() => {
    dispatch(fetchApplyIdStatus());
  }, [dispatch]);

  useEffect(() => {
    if (userId) {
      dispatch(getOnboardingOwnerData(Number(userId)));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (userId) {
      dispatch(getAnnatureCheckStatus(Number(userId)));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (
      applyIdStatusData?.status === false &&
      applyIdStatusData?.isDemoAccount === false
    ) {
      intervalRef.current = setInterval(() => {
        dispatch(fetchApplyIdStatus());
      }, APPLY_ID_STATUS_POLL_INTERVAL);
    }
    return () => clearInterval(intervalRef.current);
  }, [applyIdStatusData, dispatch]);

  useEffect(() => {
    dispatch(getConnectedSoftware());
  }, [dispatch]);

  return (
    <div className="steps-wrap">
      {requiredSteps.map((step, index) => {
        const Component = step.component;
        const stepName = resolveStepName(step);
        const isTabLoaded = loadedTabs.has(index);
        const isCompleted = isStepCompleted(stepName);
        const isInProgress = isStepInProgress(stepName);
        const isDisabled = Boolean(step.disabled);

        return (
          <div
            className={`steps-container ${activeIndex === index ? "active-step" : ""} ${isDisabled ? "disabled-step" : ""}`}
            key={index}
            ref={(el) => {
              stepRefs.current[index] = el;
            }}
          >
            <div className="steps-title-wrap">
              <div
                onClick={() =>
                  !isDisabled && toggleAccordion(index, step.title, Component)
                }
                className="steps-title"
              >
                <div className="left-section">
                  <span
                    className={`steps-icon ${isCompleted ? "completed-icon" : ""}`}
                  >
                    {isCompleted ? <LuCheck /> : step.icon}
                  </span>
                  <div className="title-content">
                    {` ${step.title}`}
                    <small className="text-muted">{step.subtitle}</small>
                  </div>
                </div>
                <div className="right-section">
                  <span
                    className={`status-progress me-2 ${activeIndex === index ? "active-status" : ""} ${isCompleted ? "completed-status" : ""} ${isInProgress ? "inprogress-status" : ""}`}
                  >
                    {stepStatusMap[stepName] || START}
                  </span>
                  <span className="dropdown-arrow">
                    <LuChevronDown />
                  </span>
                </div>
              </div>
            </div>

            {isTabLoaded && (
              <div
                className={`steps-content-collapse ${activeIndex === index ? "is-open" : ""}`}
              >
                <div className="steps-content">
                  <div className="steps-content-inner">
                    <Suspense fallback={<StepLoadingFallback />}>
                      <Component
                        {...step.props}
                        status={stepStatusMap[stepName]}
                      />
                    </Suspense>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {waitingSteps.length > 0 && (
        <>
          <div className="label-title mt-4">
            <h3>{newOnboardingSteps?.waitingStepTitle}</h3>
          </div>

          {waitingSteps.map((step, index) => {
            const waitingIndex = requiredSteps.length + index;
            const Component = step.component;
            const isTabLoaded = loadedTabs.has(waitingIndex);
            const isDisabled = Boolean(step.disabled);

            return (
              <div
                className={`steps-container ${activeIndex === waitingIndex ? "active-step" : ""} ${isDisabled ? "disabled-step" : ""}`}
                key={waitingIndex}
                ref={(el) => {
                  stepRefs.current[waitingIndex] = el;
                }}
              >
                <div className="steps-title-wrap">
                  <div
                    onClick={() =>
                      !isDisabled &&
                      toggleAccordion(waitingIndex, step.title, Component)
                    }
                    className="steps-title"
                  >
                    <div className="left-section">
                      <span
                        className={`steps-icon ${step.completed ? "completed-icon" : ""}`}
                      >
                        {step.completed ? <LuCheck /> : step.icon}
                      </span>
                      <div className="title-content">
                        {` ${step.title}`}
                        <small className="text-muted">{step.subtitle}</small>
                      </div>
                    </div>

                    <div className="right-section">
                      <span
                        className={`status-progress me-2 ${step.completed ? "completed-status" : "waiting-status"}`}
                      >
                        {step.completed ? COMPLETED : WAITING_START}
                      </span>

                      <span className="dropdown-arrow">
                        <LuChevronDown />
                      </span>
                    </div>
                  </div>
                </div>

                {isTabLoaded && (
                  <div
                    className={`steps-content-collapse ${activeIndex === waitingIndex ? "is-open" : ""}`}
                  >
                    <div className="steps-content">
                      <div className="steps-content-inner">
                        <Suspense fallback={<StepLoadingFallback />}>
                          <Component
                            {...step.props}
                            status={stepStatusMap[step.title]}
                          />
                        </Suspense>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default Steps;
